import { createContext, useContext, useState, useCallback } from 'react';

const LanguageContext = createContext(null);

export const useLanguage = () => useContext(LanguageContext);

// Translations
const translations = {
  en: {
    // Navigation
    lobby: "Lobby",
    casinoGames: "Casino Games",
    features: "Features",
    rewards: "Rewards",
    predictions: "Predictions",
    tournaments: "Tournaments",
    aiCreator: "AI Creator",
    leaderboard: "Leaderboard",
    chatRoom: "Chat Room",
    vipRewards: "VIP Rewards",
    referEarn: "Refer & Earn",
    promotions: "Promotions",
    withdraw: "Withdraw",
    buyCredits: "Buy Credits",
    liveSupport: "Live Support",
    
    // Games
    aviator: "Aviator",
    crash: "Crash",
    coinOnLemon: "Coin on Lemon",
    limbo: "Limbo",
    keno: "Keno",
    dice: "Dice",
    slots: "Slots",
    mines: "Mines",
    plinko: "Plinko",
    wheel: "Wheel",
    coinBalance: "Coin Balance",
    
    // Common
    play: "Play",
    bet: "Bet",
    cashout: "Cash Out",
    balance: "Balance",
    amount: "Amount",
    win: "Win",
    loss: "Loss",
    history: "History",
    settings: "Settings",
    profile: "Profile",
    logout: "Logout",
    login: "Login",
    register: "Register",
    email: "Email",
    password: "Password",
    name: "Name",
    submit: "Submit",
    cancel: "Cancel",
    confirm: "Confirm",
    close: "Close",
    search: "Search",
    loading: "Loading...",
    error: "Error",
    success: "Success",
    
    // Dashboard
    welcomeBack: "Welcome Back",
    yourBalance: "Your Balance",
    instantPayouts: "Instant Payouts",
    onlinePlayers: "Online Players",
    totalPaidOut: "Total Paid Out",
    liveWins: "Live Wins",
    viewAll: "View All",
    maxWin: "Max Win",
    playNow: "Play Now",
    startPlaying: "Start Playing",
    watchDemo: "Watch Demo",
    
    // Games Common
    betAmount: "Bet Amount",
    autoCashout: "Auto Cashout",
    placeBet: "Place Bet",
    waitNextRound: "Wait for next round",
    recentCrashes: "Recent Crashes",
    noGamesYet: "No games yet",
    gameInfo: "Game Info",
    
    // Crash Game
    realTimeMultiplayer: "Real-time multiplayer crash game!",
    startingIn: "Starting in",
    placeYourBets: "Place your bets!",
    crashed: "CRASHED!",
    cashedOut: "Cashed Out!",
    liveBets: "Live Bets",
    
    // Admin
    adminDashboard: "Admin Dashboard",
    manageYourPlatform: "Manage your platform",
    totalUsers: "Total Users",
    totalWagered: "Total Wagered",
    grossRevenue: "Gross Revenue",
    pendingWithdrawals: "Pending Withdrawals",
    users: "Users",
    withdrawals: "Withdrawals",
    approve: "Approve",
    reject: "Reject",
    searchUsers: "Search users by email or name...",
    userDetails: "User Details",
    addBalance: "Add Balance",
    setVip: "Set VIP",
    makeAdmin: "Make Admin",
    removeAdmin: "Remove Admin",
    banUser: "Ban User",
    unban: "Unban",
    adminAccessRequired: "Admin Access Required",
    noPermission: "You don't have permission to access this page.",
    
    // VIP
    vipLevel: "VIP Level",
    rakeback: "Rakeback",
    dailyBonus: "Daily Bonus",
    claimBonus: "Claim Bonus",
    
    // Withdraw
    withdrawFunds: "Withdraw Funds",
    withdrawalMethod: "Withdrawal Method",
    bankTransfer: "Bank Transfer",
    upiTransfer: "UPI Transfer",
    cryptocurrency: "Cryptocurrency",
    accountDetails: "Account Details",
    requestWithdrawal: "Request Withdrawal",
    withdrawalHistory: "Withdrawal History",
    
    // Responsible Gaming
    responsibleGaming: "Responsible Gaming",
    activitySummary: "Activity Summary",
    depositLimits: "Deposit Limits",
    takeABreak: "Take a Break",
    selfExclusion: "Self-Exclusion",
    dailyLimit: "Daily Limit",
    weeklyLimit: "Weekly Limit",
    monthlyLimit: "Monthly Limit",
    
    // Notifications
    notifications: "Notifications",
    markAllRead: "Mark all read",
    noNotifications: "No notifications yet",
    bigWin: "Big Win!",
    bonusCredited: "Bonus Credited!",
    tournamentStarting: "Tournament Starting!",
    depositSuccessful: "Deposit Successful",
    withdrawalApproved: "Withdrawal Approved",
    
    // Footer
    playResponsibly: "18+ | Play Responsibly",
    
    // Errors
    invalidBetAmount: "Invalid bet amount",
    insufficientBalance: "Insufficient balance",
    notConnected: "Not connected to game server",
    connectionLost: "Connection lost. Reconnecting...",
    reconnected: "Reconnected!",
  },
  
  hi: {
    // Navigation
    lobby: "लॉबी",
    casinoGames: "कैसीनो गेम्स",
    features: "फीचर्स",
    rewards: "रिवॉर्ड्स",
    predictions: "प्रिडिक्शन्स",
    tournaments: "टूर्नामेंट्स",
    aiCreator: "AI क्रिएटर",
    leaderboard: "लीडरबोर्ड",
    chatRoom: "चैट रूम",
    vipRewards: "VIP रिवॉर्ड्स",
    referEarn: "रेफर करें और कमाएं",
    promotions: "प्रमोशन्स",
    withdraw: "निकासी",
    buyCredits: "क्रेडिट खरीदें",
    liveSupport: "लाइव सपोर्ट",
    
    // Games
    aviator: "एविएटर",
    crash: "क्रैश",
    coinOnLemon: "कॉइन ऑन लेमन",
    limbo: "लिम्बो",
    keno: "कीनो",
    dice: "डाइस",
    slots: "स्लॉट्स",
    mines: "माइंस",
    plinko: "प्लिंको",
    wheel: "व्हील",
    coinBalance: "कॉइन बैलेंस",
    
    // Common
    play: "खेलें",
    bet: "बेट",
    cashout: "कैश आउट",
    balance: "बैलेंस",
    amount: "राशि",
    win: "जीत",
    loss: "हार",
    history: "इतिहास",
    settings: "सेटिंग्स",
    profile: "प्रोफाइल",
    logout: "लॉगआउट",
    login: "लॉगिन",
    register: "रजिस्टर",
    email: "ईमेल",
    password: "पासवर्ड",
    name: "नाम",
    submit: "सबमिट",
    cancel: "रद्द करें",
    confirm: "पुष्टि करें",
    close: "बंद करें",
    search: "खोजें",
    loading: "लोड हो रहा है...",
    error: "त्रुटि",
    success: "सफल",
    
    // Dashboard
    welcomeBack: "वापसी पर स्वागत है",
    yourBalance: "आपका बैलेंस",
    instantPayouts: "तुरंत भुगतान",
    onlinePlayers: "ऑनलाइन खिलाड़ी",
    totalPaidOut: "कुल भुगतान",
    liveWins: "लाइव जीत",
    viewAll: "सभी देखें",
    maxWin: "अधिकतम जीत",
    playNow: "अभी खेलें",
    startPlaying: "खेलना शुरू करें",
    watchDemo: "डेमो देखें",
    
    // Games Common
    betAmount: "बेट राशि",
    autoCashout: "ऑटो कैश आउट",
    placeBet: "बेट लगाएं",
    waitNextRound: "अगले राउंड की प्रतीक्षा करें",
    recentCrashes: "हाल के क्रैश",
    noGamesYet: "अभी तक कोई गेम नहीं",
    gameInfo: "गेम जानकारी",
    
    // Crash Game
    realTimeMultiplayer: "रीयल-टाइम मल्टीप्लेयर क्रैश गेम!",
    startingIn: "शुरू हो रहा है",
    placeYourBets: "अपना बेट लगाएं!",
    crashed: "क्रैश!",
    cashedOut: "कैश आउट हो गया!",
    liveBets: "लाइव बेट्स",
    
    // Admin
    adminDashboard: "एडमिन डैशबोर्ड",
    manageYourPlatform: "अपना प्लेटफॉर्म मैनेज करें",
    totalUsers: "कुल उपयोगकर्ता",
    totalWagered: "कुल वेजर",
    grossRevenue: "सकल राजस्व",
    pendingWithdrawals: "लंबित निकासी",
    users: "उपयोगकर्ता",
    withdrawals: "निकासी",
    approve: "मंजूर करें",
    reject: "अस्वीकार करें",
    searchUsers: "ईमेल या नाम से खोजें...",
    userDetails: "उपयोगकर्ता विवरण",
    addBalance: "बैलेंस जोड़ें",
    setVip: "VIP सेट करें",
    makeAdmin: "एडमिन बनाएं",
    removeAdmin: "एडमिन हटाएं",
    banUser: "उपयोगकर्ता को बैन करें",
    unban: "अनबैन",
    adminAccessRequired: "एडमिन एक्सेस आवश्यक",
    noPermission: "आपको इस पेज तक पहुंचने की अनुमति नहीं है।",
    
    // VIP
    vipLevel: "VIP स्तर",
    rakeback: "रेकबैक",
    dailyBonus: "दैनिक बोनस",
    claimBonus: "बोनस क्लेम करें",
    
    // Withdraw
    withdrawFunds: "फंड निकालें",
    withdrawalMethod: "निकासी विधि",
    bankTransfer: "बैंक ट्रांसफर",
    upiTransfer: "UPI ट्रांसफर",
    cryptocurrency: "क्रिप्टोकरेंसी",
    accountDetails: "खाता विवरण",
    requestWithdrawal: "निकासी अनुरोध",
    withdrawalHistory: "निकासी इतिहास",
    
    // Responsible Gaming
    responsibleGaming: "जिम्मेदार गेमिंग",
    activitySummary: "गतिविधि सारांश",
    depositLimits: "जमा सीमाएं",
    takeABreak: "ब्रेक लें",
    selfExclusion: "स्व-बहिष्करण",
    dailyLimit: "दैनिक सीमा",
    weeklyLimit: "साप्ताहिक सीमा",
    monthlyLimit: "मासिक सीमा",
    
    // Notifications
    notifications: "सूचनाएं",
    markAllRead: "सभी पढ़ें",
    noNotifications: "अभी तक कोई सूचना नहीं",
    bigWin: "बड़ी जीत!",
    bonusCredited: "बोनस क्रेडिट!",
    tournamentStarting: "टूर्नामेंट शुरू हो रहा है!",
    depositSuccessful: "जमा सफल",
    withdrawalApproved: "निकासी मंजूर",
    
    // Footer
    playResponsibly: "18+ | जिम्मेदारी से खेलें",
    
    // Errors
    invalidBetAmount: "अमान्य बेट राशि",
    insufficientBalance: "अपर्याप्त बैलेंस",
    notConnected: "गेम सर्वर से कनेक्ट नहीं",
    connectionLost: "कनेक्शन टूट गया। पुनः कनेक्ट हो रहा है...",
    reconnected: "पुनः कनेक्ट हो गया!",
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('betindia_language') || 'en';
  });

  const changeLanguage = useCallback((lang) => {
    setLanguage(lang);
    localStorage.setItem('betindia_language', lang);
  }, []);

  const t = useCallback((key) => {
    return translations[language]?.[key] || translations['en']?.[key] || key;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t, languages: ['en', 'hi'] }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageProvider;
